import React from 'react';
import {observer} from 'mobx-react';
import {Display, Layout, InteractiveContext, Panel, SearchBox, FancyFilter} from 'shared/components';
import FancyFilterPills from 'shared/components/tables/interactive/components/fancy_filter/FancyFilterPills';
import {auth, endpoints, types} from 'main/tools';
import {withState, t, locale} from 'shared/core';
import useModal from 'shared/hooks/useModal';
import {CalendarState} from '../state';
import {CalendarEventFactory} from 'main/stores/calendar_events';
import EventDetailsModal from './EventDetailsModal';

function fancyFilters() {
  const filters = ['location', 'department', 'employment_type'];

  if (auth.hasAccess('::MANAGE_TIME_OFF')) {
    filters.push('pto_policy');
  }

  return filters;
}

const FilterRow = observer(({...rest}) => {
  return (
    <div className='FilterRow flex align-items-middle bg-white mb2 pb2 px2 mxn2 border-bottom'>
      <div className='flex flex-wrap flex-grow row-gap-2'>
        <SearchBox {...rest}/>
        <div className='ml1 flex align-items-start'>
          <FancyFilterPills {...rest}/>
        </div>
      </div>
      <div className='FilterGroup-searchable flex align-items-center flex-wrap ml2'>
        <FancyFilter filters={fancyFilters()} {...rest}/>
      </div>
    </div>
  );
});

const Calendar = observer(({models, isUpdating, uiState}) => {
  const {FullCalendar, dayGridPlugin, timeGridPlugin, frLocale} = uiState;

  return (
    <React.Fragment>
      {isUpdating && <div className='CalendarOverlay'></div>}
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin]}
        initialView='dayGridMonth'
        events={models.map(model => model.toFullCalendarEvent())}
        headerToolbar={{
          left: 'prev,next',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek'
        }}
        dayMaxEvents
        eventClick={(info) => info.event.extendedProps.canViewDetails && uiState.openEventDetailsModal(info.event)}
        eventDidMount={(info) => {
          if (!info.event.extendedProps.canViewDetails) info.el.style.cursor = 'default';
        }}
        datesSet={(info) => uiState.updateDates(info.start.toISOString(), info.end.toISOString())}
        locale={locale === 'fr' && frLocale}
      />
    </React.Fragment>
  );
});

const CalendarContainer = observer(({uiState}) => {
  const {startDate, endDate} = uiState;

  const eventDetailsModalOpen = useModal(uiState, 'EventDetailsModal', 'selectedEvent');

  return (
    <div>
      <Display title={t('calendar.Calendar')}/>
      <Layout>
        <Panel className='relative'>
          <InteractiveContext
            onMount={agent => uiState.setInteractiveAgent(agent)}
            saveStateToUrl
            uiState={uiState}
            proxy={{
              type: 'async',
              endpoint: endpoints.CALENDAR_EVENTS.with(startDate, endDate),
              modelType: types.CALENDAR_EVENT,
              model: CalendarEventFactory
            }}
          >
            <FilterRow/>
            <Calendar/>
          </InteractiveContext>
        </Panel>
        <EventDetailsModal uiState={uiState} modalOpen={eventDetailsModalOpen}/>
      </Layout>
    </div>
  );
});

export default withState(CalendarContainer, CalendarState);
